import * as Yup from "yup";
import CustomFields from "./routeRate/CustomFields";

// validation rule while adding new rate card into the database
export const getRouteRateValidationSchema = () => {
  const validationSchema = Yup.object().shape({
    baseRate: Yup.number().min(0).required("Base rate is required"),
    variableRate: Yup.number().min(0).required("Variable rate is required"),
    variableRateType: Yup.string().required("Variable rate type is required"),
    routeMinimum: Yup.number().min(0).required("Route minimum is required"),
    lateCancellationCharge: Yup.number()
      .min(0)
      .required("Late cancellation charge is required"),

    // discount validations
    ...discountValidations,

    // fuel surcharge validations
    ...fuelSurchargeValidations,

    // peak surcharge validations
    ...peakSurchargeValidations,

    ...customFieldsValidations,
  });

  return validationSchema;
};

const discountValidations = {
  discount_type: Yup.string().when("show_discount", (show_discount, schema) => {
    const variableType = typeof show_discount;
    const isShowDiscount =
      (variableType === "boolean" && show_discount) ||
      (variableType === "object" && show_discount[0]);
    if (isShowDiscount)
      return schema.required("At least one of the discount types is required");
    return schema.optional();
  }),
  // VOLUME
  discount_volume_min_unit: Yup.number().when(
    ["show_discount", "discount_type"],
    ([show_discount, discount_type], schema) => {
      const variableType = typeof show_discount;
      const isShowDiscount =
        (variableType === "boolean" && show_discount) ||
        (variableType === "object" && show_discount[0]);
      if (isShowDiscount && discount_type === "volume")
        return schema.required("Please fill this field");

      return schema.optional();
    }
  ),
  discount_volume_frequency: Yup.string().when(
    ["show_discount", "discount_type"],
    ([show_discount, discount_type], schema) => {
      const variableType = typeof show_discount;
      const isShowDiscount =
        (variableType === "boolean" && show_discount) ||
        (variableType === "object" && show_discount[0]);
      if (isShowDiscount && discount_type === "volume")
        return schema.required("Please choose any one");

      return schema.optional();
    }
  ),
  discount_volume_unit_type: Yup.string().when(
    ["show_discount", "discount_type"],
    ([show_discount, discount_type], schema) => {
      const variableType = typeof show_discount;
      const isShowDiscount =
        (variableType === "boolean" && show_discount) ||
        (variableType === "object" && show_discount[0]);
      if (isShowDiscount && discount_type === "volume")
        return schema.required(
          "At least one of the volume value types is required"
        );
      return schema.optional().nullable();
    }
  ),
  discount_volume_value_percentage: Yup.number()
    .max(100, "Percentage accpets a max value of 100!")
    .when(
      ["show_discount", "discount_type", "discount_volume_unit_type"],
      ([show_discount, discount_type, discount_volume_unit_type], schema) => {
        const variableType = typeof show_discount;
        const isShowDiscount =
          (variableType === "boolean" && show_discount) ||
          (variableType === "object" && show_discount[0]);
        if (
          isShowDiscount &&
          discount_type === "volume" &&
          discount_volume_unit_type === "percentage"
        )
          return schema.required("Please fill this field");
        return schema.optional();
      }
    ),
    discount_volume_value_dollar: Yup.number()
    .when(
      ["show_discount", "discount_type", "discount_volume_unit_type"],
      ([show_discount, discount_type, discount_volume_unit_type], schema) => {
        const variableType = typeof show_discount;
        const isShowDiscount =
          (variableType === "boolean" && show_discount) ||
          (variableType === "object" && show_discount[0]);
        if (
          isShowDiscount &&
          discount_type === "volume" &&
          discount_volume_unit_type === "value"
        )
          return schema.required("Please fill this field");
        return schema.optional();
      }
    ),
  
  // RATE
  discount_rate_type: Yup.string().when(
    ["show_discount", "discount_type"],
    ([show_discount, discount_type], schema) => {
      const variableType = typeof show_discount;
      const isShowDiscount =
        (variableType === "boolean" && show_discount) ||
        (variableType === "object" && show_discount[0]);
      if (isShowDiscount && discount_type === "rate")
        return schema.required("Please choose any one");

      return schema.optional();
    }
  ),
  discount_rate_min_unit: Yup.number().when(
    ["show_discount", "discount_type"],
    ([show_discount, discount_type], schema) => {
      const variableType = typeof show_discount;
      const isShowDiscount =
        (variableType === "boolean" && show_discount) ||
        (variableType === "object" && show_discount[0]);
      if (isShowDiscount && discount_type === "rate")
        return schema.required("Please fill this field");

      return schema.optional();
    }
  ),
  discount_rate_frequency: Yup.string().when(
    ["show_discount", "discount_type"],
    ([show_discount, discount_type], schema) => {
      const variableType = typeof show_discount;
      const isShowDiscount =
        (variableType === "boolean" && show_discount) ||
        (variableType === "object" && show_discount[0]);
      if (isShowDiscount && discount_type === "rate")
        return schema.required("Please choose any one");

      return schema.optional();
    }
  ),
  discount_rate_unit_type: Yup.string().when(
    ["show_discount", "discount_type"],
    ([show_discount, discount_type], schema) => {
      const variableType = typeof show_discount;
      const isShowDiscount =
        (variableType === "boolean" && show_discount) ||
        (variableType === "object" && show_discount[0]);
      if (isShowDiscount && discount_type === "rate")
        return schema.required(
          "At least one of the rate value types is required"
        );
      return schema.optional().nullable();
    }
  ),
  discount_rate_value_percentage: Yup.number()
    .max(100, "Percentage accpets a max value of 100!")
    .when(
      ["show_discount", "discount_type", "discount_rate_unit_type"],
      ([show_discount, discount_type, discount_rate_unit_type], schema) => {
        const variableType = typeof show_discount;
        const isShowDiscount =
          (variableType === "boolean" && show_discount) ||
          (variableType === "object" && show_discount[0]);
        if (
          isShowDiscount &&
          discount_type === "rate" &&
          discount_rate_unit_type === "percentage"
        )
          return schema.required("Please fill this field");
        return schema.optional();
      }
    ),
  discount_rate_value_dollar: Yup.number().when(
    ["show_discount", "discount_type", "discount_rate_unit_type"],
    ([show_discount, discount_type, discount_rate_unit_type], schema) => {
      const variableType = typeof show_discount;
      const isShowDiscount =
        (variableType === "boolean" && show_discount) ||
        (variableType === "object" && show_discount[0]);
      if (
        isShowDiscount &&
        discount_type === "rate" &&
        discount_rate_unit_type === "value"
      )
        return schema.required("Please fill this field");
      return schema.optional();
    }
  ),
};

const fuelSurchargeValidations = {
  fuelType: Yup.string().when(
    "show_fuel_surcharge",
    (show_fuel_surcharge, schema) => {
      const variableType = typeof show_fuel_surcharge;
      const isShowFuelSurcharge =
        (variableType === "boolean" && show_fuel_surcharge) ||
        (variableType === "object" && show_fuel_surcharge[0]);
      if (isShowFuelSurcharge)
        return schema.required("Please select the fuel type");
      return schema.optional();
    }
  ),
  regionName: Yup.string().when(
    "show_fuel_surcharge",
    (show_fuel_surcharge, schema) => {
      const variableType = typeof show_fuel_surcharge;
      const isShowFuelSurcharge =
        (variableType === "boolean" && show_fuel_surcharge) ||
        (variableType === "object" && show_fuel_surcharge[0]);
      if (isShowFuelSurcharge) return schema.required("Please select a region");
      return schema.optional();
    }
  ),

  // fuel surcharge fields
  fuelSurchargeFields: Yup.array().when(
    "show_fuel_surcharge",
    (show_fuel_surcharge, schema) => {
      const variableType = typeof show_fuel_surcharge;
      const isShowFuelSurcharge =
        (variableType === "boolean" && show_fuel_surcharge) ||
        (variableType === "object" && show_fuel_surcharge[0]);
      if (isShowFuelSurcharge) {
        return Yup.array()
          .min(1)
          .of(
            Yup.object().shape({
              fieldName: Yup.string().required("Required"),
              fieldOperator: Yup.string().required("Required"),
            })
          );
      }
      return Yup.array().optional();
    }
  ),

  fuelSurchargeLookup: Yup.array().when(
    "show_fuel_surcharge",
    (show_fuel_surcharge, schema) => {
      const variableType = typeof show_fuel_surcharge;
      const isShowFuelSurcharge =
        (variableType === "boolean" && show_fuel_surcharge) ||
        (variableType === "object" && show_fuel_surcharge[0]);
      if (isShowFuelSurcharge) {
        return Yup.array().min(1).required("Please upload the file");
        // .of(
        //   Yup.object().shape({
        //     fscFile: Yup.string().required("Please upload the file"),
        //   })
        // );
      }
      return Yup.array().optional();
    }
  ),
};

const peakSurchargeValidations = {
  peakSurcharge: Yup.array().when(
    "show_peak_surcharge",
    (show_peak_surcharge, schema) => {
      const variableType = typeof show_peak_surcharge;
      const isShowPeakSurcharge =
        (variableType === "boolean" && show_peak_surcharge) ||
        (variableType === "object" && show_peak_surcharge[0]);
      if (isShowPeakSurcharge) {
        return Yup.array().of(
          Yup.object().shape({
            peak_surcharge_date_range: Yup.array()
              .min(2)
              .required("Please select the date range"),
            peakSurchargeType: Yup.string().required("required"),
            // peak_surcharge_value_percentage: Yup.number().required("required"),
            // peak_surcharge_value_dollar: Yup.number().required("required"),
            peak_surcharge_value_percentage: Yup.number()
              .max(100, "Percentage accpets a max value of 100!")
              .when(
                ["peakSurchargeType", "peak_surcharge_date_range"],
                ([peakSurchargeType, peak_surcharge_date_range], schema) => {
                  if (peakSurchargeType === "percentage")
                    return schema.required("required");
                  return schema.optional();
                }
              ),
            peak_surcharge_value_dollar: Yup.number().when(
              ["peakSurchargeType"],
              ([peakSurchargeType], schema) => {
                if (peakSurchargeType === "value")
                  return schema.required("required");
                return schema.optional();
              }
            ),
          })
        );
      }
      return Yup.array().optional();
    }
  ),
};

const customFieldsValidations = {
  customFields: Yup.array().when(
    "show_custom_field",
    (show_custom_field, schema) => {
      const variableType = typeof show_custom_field;
      const isShowCustomField =
        (variableType === "boolean" && show_custom_field) ||
        (variableType === "object" && show_custom_field[0]);

      if (isShowCustomField) {
        return Yup.array()
          .min(1, "At least one custom field is required")
          .of(
            Yup.object().shape({
              fieldName: Yup.string().required("Field name is required"),
              fieldValue: Yup.string().required("Field value is required"),
            })
          );
      }
      return Yup.array().optional();
    }
  ),
};

